import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { CreateIcon } from '@savvy-kit/foundations/icons';
import { cn } from '@savvy-kit/lib/utils';
import {
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '@savvy-kit/templates/sidebar';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SearchIcon } from 'lucide-react';
import { ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY } from 'src/components/advisor-dashboard/layout/constants';
import SavvySLogo from 'src/components/logo/savvy-s-logo';
import TrackedButton from 'src/components/tracked/tracked-button';
import { useDebounce } from 'src/hooks';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';

import CanvasMenuButton from './canvas-menu-button';
import GlobalSearchDialog from './global-search-dialog';
import GlobalSearchDialogWithAttribution from './global-search-dialog-with-attribution';
import NotificationsMenuButton from './notifications-menu-button';

function AdvisorSideNavHeader() {
  const { state, setOpen } = useSidebar();
  const { flagHouseholdSearchMatchAttribution, flagNotificationsV2 } = useFlags();
  const { user } = useAuth();
  const currentUserId = user?.id?.toString() ?? null;
  const router = useRouter();
  const debouncedSideNavState = useDebounce(state, 250);
  const isSideNavExpanded = state === 'expanded';

  const [isOpenSearchDialog, setIsOpenSearchDialog] = useState(false);

  useEffect(() => {
    const TYPEAHEAD_WIDGET_SELECTOR =
      '[role="combobox"],[role="listbox"],[role="option"],[role="menu"],[role="menuitem"],[role="menuitemcheckbox"],[role="menuitemradio"],[role="tree"],[role="treeitem"]';

    const handleKeyDown = (event: KeyboardEvent) => {
      const isHtmlElement = event.target instanceof HTMLElement;
      const isInputElement = isHtmlElement && event.target.tagName.toLowerCase() === 'input';
      const isTextAreaElement = isHtmlElement && event.target.tagName.toLowerCase() === 'textarea';
      const isEditableText =
        isHtmlElement && (event.target.isContentEditable || event.target.classList.contains('editor-input'));
      const isListItem = isHtmlElement && event.target.tagName.toLowerCase() === 'li';
      const isInTypeaheadWidget = isHtmlElement && event.target.closest(TYPEAHEAD_WIDGET_SELECTOR) !== null;
      // Avoid `document.body` overflow as a proxy for “modal open”: test runners and layouts
      // often set `overflow: hidden` on body without a dialog, which would swallow Cmd/Ctrl+K.
      const hasOpenAppDialog = document.querySelector('[data-slot="dialog-overlay"][data-open]') !== null;

      if (
        !isInputElement &&
        !isTextAreaElement &&
        !isEditableText &&
        !isListItem &&
        !isInTypeaheadWidget &&
        !hasOpenAppDialog
      ) {
        const key = event.key.toLowerCase();

        if (key === 'k' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          setIsOpenSearchDialog(true);
        }

        if (key === 'c' && !(event.ctrlKey || event.metaKey)) {
          router.push('/advisor/dashboard/tasks/new');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [router]);

  const savvyLogoHref = '/advisor/canvas';

  return (
    <SidebarHeader className="relative">
      <SidebarMenu className="mb-2 mt-4">
        <SidebarMenuItem
          className="flex justify-between items-center group/logo px-1"
          onClick={() => {
            if (!isSideNavExpanded) {
              setOpen(true);
              sessionStorage.setItem(ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY, 'true');
            }
          }}
        >
          <SidebarMenuButton
            size="lg"
            render={<div className="flex justify-between w-full" />}
            className="hover:bg-transparent! p-0 h-[32px]"
          >
            <NextLink href={savvyLogoHref} aria-label="Savvy Logo" className="size-8 p-1 text-foreground">
              <SavvySLogo width={24} height={24} />
            </NextLink>
          </SidebarMenuButton>
          {isSideNavExpanded && (
            <div>
              <SidebarTrigger
                onClick={() => {
                  sessionStorage.setItem(ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY, 'false');
                }}
              />
            </div>
          )}
          {!isSideNavExpanded && debouncedSideNavState === 'collapsed' && (
            <div className="hidden group-hover/logo:block">
              <SidebarTrigger
                onClick={() => {
                  sessionStorage.setItem(ADVISOR_SIDE_NAV_EXPANDED_SESSION_STORAGE_KEY, 'true');
                }}
              />
            </div>
          )}
        </SidebarMenuItem>
        <SidebarMenuItem
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <TrackedButton
            elementName="top-nav-global-create-button"
            className="mb-4 mt-7 w-full overflow-hidden"
            variant="secondary"
            icon={<CreateIcon />}
            onClick={() => {
              router.push('/advisor/dashboard/tasks/new');
            }}
          >
            <span className={cn(!isSideNavExpanded && 'hidden')}>Create</span>
          </TrackedButton>
        </SidebarMenuItem>
        <SidebarMenuItem
          className={cn(!isSideNavExpanded && 'px-1')}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {flagHouseholdSearchMatchAttribution ? (
            <GlobalSearchDialogWithAttribution
              open={isOpenSearchDialog}
              onOpenChange={setIsOpenSearchDialog}
              userId={currentUserId}
            >
              <SidebarMenuButton tooltip="Search">
                <SearchIcon />
                <span>Search</span>
              </SidebarMenuButton>
            </GlobalSearchDialogWithAttribution>
          ) : (
            <GlobalSearchDialog
              open={isOpenSearchDialog}
              onOpenChange={setIsOpenSearchDialog}
              dialogTriggerRender={
                <SidebarMenuButton tooltip="Search">
                  <SearchIcon />
                  <span>Search</span>
                </SidebarMenuButton>
              }
            />
          )}
        </SidebarMenuItem>
        {!flagNotificationsV2 && (
          <SidebarMenuItem
            className={cn(!isSideNavExpanded && 'px-1')}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <NotificationsMenuButton />
          </SidebarMenuItem>
        )}
        <SidebarMenuItem
          className={cn(!isSideNavExpanded && 'px-1')}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CanvasMenuButton />
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarSeparator className="absolute bottom-0 left-0 right-0" />
    </SidebarHeader>
  );
}

export default AdvisorSideNavHeader;
